import { FC, ReactNode, createContext, useContext, useState, useMemo } from 'react';
import type { Animal } from '@/model/Animal.model';
import type { GlossaryItem } from '@/model/Glosary.model';
import type { StudyApiId } from '@/model/Study.model';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { DateUtils } from '@/utils/Date.utils';
import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import {
  submitBulkObservations,
  submitNormalObservations,
  type ObservationRowData,
} from './RecordBulkObservations.utils';

export const MAX_BULK_OBSERVATION_RECORDS = 2500;

interface RecordBulkObservationsContextValue {
  // Step management
  currentStep: 1 | 2;
  goToStep2: () => void;
  goToStep1: () => void;

  // Step 1 state
  animals: Array<Animal>;
  observations: Array<GlossaryItem> | undefined;
  observationsLoading: boolean;
  selectedObservationIds: Array<string>;
  setSelectedObservationIds: (ids: Array<string>) => void;
  selectedObservations: Array<GlossaryItem>;
  observedDate: string;
  setObservedDate: (date: string) => void;

  // Step 2 state
  tableData: Array<ObservationRowData>;
  setTableData: (data: Array<ObservationRowData>) => void;

  // Actions
  handleRecordNormal: () => Promise<void>;
  handleSubmit: (tableData: Array<ObservationRowData>) => Promise<void>;

  // Loading states
  recordingNormal: boolean;
  submitting: boolean;
  submissionError: Error | null;

  // Validation
  exceedsRecordNormalLimit: boolean;
  exceedsNextLimit: boolean;
  totalRecordsToCreate: number;

  // Callbacks
  closeModal: () => void;
  handleCallback: () => void;
}

const RecordBulkObservationsContext = createContext<RecordBulkObservationsContextValue | null>(null);

export const useRecordBulkObservations = () => {
  const context = useContext(RecordBulkObservationsContext);
  if (!context) {
    throw new Error('useRecordBulkObservations must be used within RecordBulkObservationsProvider');
  }
  return context;
};

interface RecordBulkObservationsProviderProps {
  animals: Array<Animal>;
  studyApiId: StudyApiId;
  closeModal: () => void;
  handleCallback: () => void;
  children: ReactNode;
}

export const RecordBulkObservationsProvider: FC<RecordBulkObservationsProviderProps> = ({
  animals,
  studyApiId,
  closeModal,
  handleCallback,
  children,
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  // Step 1 state
  const [selectedObservationIds, setSelectedObservationIds] = useState<Array<string>>([]);
  const [observedDate, setObservedDate] = useState<string>(DateUtils.dateTimeNow());

  // Step 2 state
  const [tableData, setTableData] = useState<Array<ObservationRowData>>([]);

  // Loading/error states
  const [recordingNormal, setRecordingNormal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<Error | null>(null);

  // Fetch observation types
  const { response: observationGlossaryResponse, loading: observationGlossaryLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'team-glossary.list',
    query: { group: 'observations' },
    options: { invokeOnInit: true, onError: { slug: 'fetch-observations-glossary', throw: false } },
  });

  // @ts-expect-error No types for glossary items
  const observationGlossaries = observationGlossaryResponse?.body?.data as Array<GlossaryItem> | undefined;

  // API hooks
  const { invoke: invokeBulkObservations } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/observations',
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: false, slug: 'record-bulk-observations' } },
  });

  const { invoke: invokeBulkNormalObservations } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/observations/normal',
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: false, slug: 'record-bulk-normal-observations' } },
  });

  const selectedObservations = useMemo(() => {
    const observationTypeMap = new Map(
      (observationGlossaries ?? []).map((observationType) => [observationType.id, observationType])
    );

    return selectedObservationIds.reduce<Array<GlossaryItem>>((acc, id) => {
      if (observationTypeMap.has(id)) {
        acc.push(observationTypeMap.get(id) as GlossaryItem);
      }
      return acc;
    }, []);
  }, [observationGlossaries, selectedObservationIds]);

  // Validation: Check if the number of records to create exceeds the limit
  const exceedsRecordNormalLimit = animals.length > MAX_BULK_OBSERVATION_RECORDS;
  const totalRecordsToCreate = animals.length * selectedObservationIds.length;
  const exceedsNextLimit = totalRecordsToCreate > MAX_BULK_OBSERVATION_RECORDS;

  const goToStep2 = () => {
    const initialData: Array<ObservationRowData> = selectedObservations.map((obs) => ({
      observationType: obs.title,
      glossary_id: obs.api_id,
      type: obs.type,
      score: '',
      severity: obs.type === 'severity' ? 'Mild' : '',
      comments: '',
      description: obs.description,
      options: obs.options
        ? {
            min: String(obs.options.min ?? ''),
            max: String(obs.options.max ?? ''),
          }
        : undefined,
    }));
    setTableData(initialData);
    setCurrentStep(2);
  };

  const goToStep1 = () => {
    setCurrentStep(1);
  };

  const handleRecordNormal = async () => {
    if (!_notNil(studyApiId)) {
      errorToast('Study information not found');
      return;
    }

    setRecordingNormal(true);

    try {
      const animalApiIds = animals.map((a) => a.api_id).filter(_notNil);
      await submitNormalObservations(animalApiIds, studyApiId, observedDate, invokeBulkNormalObservations);

      successToast(`Normal observations recorded for ${animals.length} animal${animals.length > 1 ? 's' : ''}`);
      closeModal();
      handleCallback();
    } catch (err) {
      const ivError = new InVivoError('Could not save normal observations', {
        slug: 'record-bulk-observations-normal',
        cause: err,
      });
      ExceptionHandler.captureException(ivError);
      errorToast('Could not save normal observations');
    } finally {
      setRecordingNormal(false);
    }
  };

  const handleSubmit = async (updatedTableData: Array<ObservationRowData>) => {
    setSubmissionError(null);
    setSubmitting(true);

    try {
      const animalApiIds = animals.map((a) => a.api_id).filter(_notNil);
      await submitBulkObservations(studyApiId, animalApiIds, updatedTableData, observedDate, invokeBulkObservations);

      // Success
      const totalObs = updatedTableData.length;
      const totalAnimals = animals.length;
      successToast(
        `${totalObs} observation${totalObs > 1 ? 's' : ''} recorded for ${totalAnimals} animal${totalAnimals > 1 ? 's' : ''}`
      );
      closeModal();
      handleCallback();
    } catch (error) {
      setSubmissionError(error as Error);
    } finally {
      setSubmitting(false);
    }
  };

  const value: RecordBulkObservationsContextValue = {
    currentStep,
    goToStep2,
    goToStep1,
    animals,
    observations: observationGlossaries,
    observationsLoading: observationGlossaryLoading,
    selectedObservationIds,
    setSelectedObservationIds,
    selectedObservations,
    observedDate,
    setObservedDate,
    tableData,
    setTableData,
    handleRecordNormal,
    handleSubmit,
    recordingNormal,
    submitting,
    submissionError,
    exceedsRecordNormalLimit,
    exceedsNextLimit,
    totalRecordsToCreate,
    closeModal,
    handleCallback,
  };

  return <RecordBulkObservationsContext.Provider value={value}>{children}</RecordBulkObservationsContext.Provider>;
};
